import { forwardRef, useState, useMemo, type InputHTMLAttributes } from 'react'
import { Eye, EyeOff, Check, X } from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * PasswordInput Component
 * Based on specs/UX/structured/code/js/password-strength.js
 *
 * Features:
 * - Password visibility toggle
 * - Strength indicator (4 levels)
 * - Requirement checklist
 * - Real-time validation feedback
 */

export interface PasswordInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string
  error?: string
  hint?: string
  /** Show password strength indicator */
  showStrength?: boolean
  /** Minimum strength level required (0-4) */
  minStrength?: number
}

interface PasswordRequirement {
  label: string
  test: (password: string) => boolean
}

const requirements: PasswordRequirement[] = [
  { label: 'Минимум 8 символа', test: (p) => p.length >= 8 },
  { label: 'Главна буква', test: (p) => /[A-ZА-Я]/.test(p) },
  { label: 'Малка буква', test: (p) => /[a-zа-я]/.test(p) },
  { label: 'Цифра', test: (p) => /\d/.test(p) },
]

const strengthLabels = ['Много слаба', 'Слаба', 'Средна', 'Добра', 'Силна']
const strengthColors = [
  'bg-error',
  'bg-error',
  'bg-warning',
  'bg-success',
  'bg-success',
]

function calculateStrength(password: string): number {
  if (!password) return 0
  return requirements.filter((req) => req.test(password)).length
}

export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  (
    {
      label,
      error,
      hint,
      showStrength = false,
      minStrength: _minStrength = 0,
      className,
      id,
      value,
      onChange,
      ...props
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = useState(false)
    const [internalValue, setInternalValue] = useState('')

    // Use controlled or uncontrolled value
    // Only use controlled mode if value is explicitly provided (not undefined)
    const isControlled = value !== undefined
    const password = isControlled ? (value as string) : internalValue

    const inputId = id || props.name
    const hasError = Boolean(error)

    const strength = useMemo(() => calculateStrength(password), [password])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      // Always update internal value for strength indicator in uncontrolled mode
      if (!isControlled) {
        setInternalValue(e.target.value)
      }
      // Call parent onChange if provided (e.g., from react-hook-form register)
      if (onChange) {
        onChange(e)
      }
    }

    return (
      <div className={cn('w-full', className)}>
        {/* Label */}
        {label && (
          <label
            htmlFor={inputId}
            className="block text-body-sm font-medium text-gray-700 mb-1.5"
          >
            {label}
            {props.required && (
              <span className="text-error ml-0.5" aria-hidden="true">
                *
              </span>
            )}
          </label>
        )}

        {/* Input wrapper */}
        <div className="relative">
          <input
            ref={ref}
            id={inputId}
            type={showPassword ? 'text' : 'password'}
            {...(isControlled ? { value: password, onChange: handleChange } : { onChange: handleChange })}
            aria-invalid={hasError}
            aria-describedby={
              error
                ? `${inputId}-error`
                : hint
                  ? `${inputId}-hint`
                  : undefined
            }
            className={cn(
              'w-full h-12 px-4 pr-10 text-body text-gray-900',
              'bg-white border rounded-md',
              'placeholder:text-gray-400',
              'transition-colors duration-small',
              'focus:outline-none focus:ring-2 focus:ring-offset-0',
              hasError
                ? 'border-error focus:ring-error/40 focus:border-error'
                : 'border-gray-300 focus:ring-accent/40 focus:border-accent'
            )}
            {...props}
          />

          {/* Toggle button */}
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
            tabIndex={-1}
            aria-label={showPassword ? 'Скрий паролата' : 'Покажи паролата'}
          >
            {showPassword ? (
              <EyeOff className="w-5 h-5" />
            ) : (
              <Eye className="w-5 h-5" />
            )}
          </button>
        </div>

        {/* Strength indicator */}
        {showStrength && password && (
          <div className="mt-3 space-y-2">
            {/* Strength bars */}
            <div className="flex gap-1">
              {Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  className={cn(
                    'h-1 flex-1 rounded-full transition-colors',
                    i < strength ? strengthColors[strength] : 'bg-gray-200'
                  )}
                />
              ))}
            </div>

            {/* Strength label */}
            <p
              className={cn(
                'text-caption font-medium',
                strength <= 1 ? 'text-error' : strength <= 2 ? 'text-warning' : 'text-success'
              )}
            >
              {strengthLabels[strength]}
            </p>

            {/* Requirements checklist */}
            <ul className="space-y-1">
              {requirements.map((req) => {
                const passed = req.test(password)
                return (
                  <li
                    key={req.label}
                    className={cn(
                      'flex items-center gap-2 text-caption',
                      passed ? 'text-success' : 'text-gray-500'
                    )}
                  >
                    {passed ? (
                      <Check className="w-3.5 h-3.5" />
                    ) : (
                      <X className="w-3.5 h-3.5" />
                    )}
                    {req.label}
                  </li>
                )
              })}
            </ul>
          </div>
        )}

        {/* Error message */}
        {error && (
          <p
            id={`${inputId}-error`}
            className="mt-1.5 text-caption text-error"
            role="alert"
          >
            {error}
          </p>
        )}

        {/* Hint text */}
        {hint && !error && !showStrength && (
          <p id={`${inputId}-hint`} className="mt-1.5 text-caption text-gray-500">
            {hint}
          </p>
        )}
      </div>
    )
  }
)

PasswordInput.displayName = 'PasswordInput'
